import { FC, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { SvgListDashes } from "../../../assets/icons/SvgListDashes";
import { CustomLineChart } from "../../../common/components/Chart/LineChart";
import { DatePicker } from "../../../common/components/DatePicker/DatePicker";
import { Tabs } from "../../../common/components/Tabs/Tabs";
import {
  orderFiltersSelector,
  resetFilter,
  setDateFrom,
  setDateTo,
  setFilterPeriod,
  setFilterVehicleCategory,
  setFilterVehicleType,
  setSearchText,
} from "./ordersSlice";
import { CompletedOrderTable } from "../tables/orders/CompletedOrderTable";
import { Route, Routes, useLocation } from "react-router-dom";
import { CanceledOrderTable } from "../tables/orders/CanceledOrderTable";
import { OrderVisorTable } from "../tables/orders/OrderViewTable";
import { SvgLoupe } from "../../../assets/icons/SvgLoupe";
import { IconInput } from "../../../common/components/Input/IconInput";
import { AdminOrdersFilter } from "common/components/Filter/AdminOrdersFilter";
import { PERIOD } from "common/utils/consts";
import { PERIOD_TABS } from "../reports/ReportToolbar";

export const OrdersPage: FC = () => {
  const location = useLocation();

  const filters = useAppSelector(orderFiltersSelector);
  const { period, dateFrom, dateTo, searchText, vehicleCategory, vehicleType } =
    filters;

  const chartData = useAppSelector(
    (state) => state.administrator.orders.chartData
  );

  const dispatch = useAppDispatch();

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);

  const handleSetActiveTab = (period: number) => {
    dispatch(setFilterPeriod(period));
  };

  const handleFilterVehiclesChange = (id: number) => {
    dispatch(setFilterVehicleCategory(id));
  };

  const handleFilterVehicleTypeChange = (id: number) => {
    dispatch(setFilterVehicleType(id));
  };

  const handleDateFromChange = (date: Nullable<Date>) => {
    dispatch(setDateFrom(date?.toISOString() || undefined));
  };

  const handleDateToChange = (date: Nullable<Date>) => {
    dispatch(setDateTo(date?.toISOString() || undefined));
  };

  const handleResetFilter = () => {
    dispatch(resetFilter());
  };

  const isOnOrdersViewPage = location.pathname === "/orders/visor";

  const showChart = period !== PERIOD.TODAY;

  const hasVehicleFilter = vehicleCategory.length > 0 || vehicleType.length > 0;

  const tableProps = {
    dateFrom,
    dateTo,
    vehicleCategory,
    vehicleType,
  };

  // Пикер дат стоит на одном месте в обоих режимах, чтобы не перемонтироваться
  // при переходе в визор и обратно и не сбрасывать выбранный период
  return (
    <>
      <div className="px-[5px]">
        {isFilterOpen && (
          <AdminOrdersFilter
            setIsFilterOpen={setIsFilterOpen}
            handleFilterVehicleCategoryChange={handleFilterVehiclesChange}
            handleFilterVehicleTypeChange={handleFilterVehicleTypeChange}
            handleFilterReset={handleResetFilter}
            stateSelector={orderFiltersSelector}
          />
        )}
        <div className="flex items-center gap-[15px]">
          {isOnOrdersViewPage ? (
            <IconInput
              className="h-[35px] w-full"
              placeholder="Поиск по заказам: номер, телефон, клиент, исполнитель"
              icon={<SvgLoupe className="h-[15px] w-[15px] text-gray" />}
              value={searchText}
              onChange={(event) => dispatch(setSearchText(event.target.value))}
            />
          ) : (
            <div className="h-[35px]">
              <Tabs
                className="h-full"
                tabs={PERIOD_TABS}
                value={period}
                onTabClick={handleSetActiveTab}
              />
            </div>
          )}
          <div>
            <DatePicker
              className="h-[35px] w-[240px]"
              onStartDateChange={handleDateFromChange}
              onEndDateChange={handleDateToChange}
              placeholderText="Введите промежуток дат"
              isClearable={true}
            />
          </div>
          <button
            className={`ml-auto flex h-[35px] items-center justify-center rounded border bg-lightWhiteGray px-[8px] text-sm font-medium
        outline-none transition hover:border-primary hover:text-primary focus:border-primary focus:text-primary active:text-black ${
          hasVehicleFilter ? "border-primary text-primary" : "border-transparent"
        }`}
            onClick={() => setIsFilterOpen((prev) => !prev)}
          >
            <SvgListDashes className="mr-[5px] h-[16px] w-[16px]" />
            Фильтр
          </button>
        </div>
      </div>
      <div>
        {!isOnOrdersViewPage && showChart && chartData && chartData.length > 1 && (
          <div className="mb-[20px]">
            <CustomLineChart chartData={chartData} />
          </div>
        )}
        <Routes>
          <Route
            path="/completed"
            element={<CompletedOrderTable {...tableProps} />}
          />
          <Route
            path="/canceled"
            element={<CanceledOrderTable {...tableProps} />}
          />
          <Route
            path="/visor"
            element={<OrderVisorTable {...tableProps} query={searchText} />}
          />
        </Routes>
      </div>
    </>
  );
};
